import { HubConnection } from "@microsoft/signalr";
import { Dispatch, useCallback } from "react";

import AppEventTarget from "@src/common/events/AppEventTarget";
import ConnectorStatusEvent from "@src/common/events/ConnectorStatusEvent";
import AnalysisCompletedEvent from "@src/common/events/AnalysisCompletedEvent";
import ShowConnectorTooltipEvent from "@src/common/events/ShowConnectorTooltipEvent";
import UpdateConnectorStatusEvent, {
  ConnectorStatusEventDetail,
} from "@src/common/events/UpdateConnectorStatusEvent";
import { PageableList } from "@src/modules/App/Notifications/shared/pageableList";
import {
  INotification,
  NotificationActionType,
} from "@src/modules/App/Notifications/shared/types";
import { NotificationEvents } from "@src/modules/App/Notifications/signalR/enums";
import {
  Actions,
  ActionTypes,
} from "@src/modules/App/Notifications/signalR/reducer/actions";

export const useListeners = () => {
  const {
    AddNotification,
    ReceiveNotifications,
    MarkedAsRead,
    SetConnectorStatus,
    UpdateConnectorStatus,
    ShowConnectorTooltip,
  } = NotificationEvents;

  const registerListeners = useCallback(
    (hub: HubConnection | undefined, dispatch: Dispatch<Actions>) => {
      if (!hub) return;

      hub.on(AddNotification, (notification: INotification) => {
        dispatch({
          type: ActionTypes.AddNotification,
          data: notification,
        });

        if (
          notification.actionType === NotificationActionType.TestFailed ||
          notification.actionType === NotificationActionType.TestPassed
        ) {
          AppEventTarget.dispatchEvent(
            new AnalysisCompletedEvent(notification),
          );
        }
      });

      hub.on(ReceiveNotifications, (page: PageableList<INotification>) =>
        dispatch({ type: ActionTypes.ReceiveNotifications, page }),
      );

      hub.on(MarkedAsRead, (notificationId?: string) =>
        dispatch({ type: ActionTypes.MarkedAsRead, notificationId }),
      );

      hub.on(SetConnectorStatus, () => {
        AppEventTarget.dispatchEvent(new ConnectorStatusEvent());
      });

      hub.on(
        UpdateConnectorStatus,
        (connectorStatus: ConnectorStatusEventDetail) =>
          AppEventTarget.dispatchEvent(
            new UpdateConnectorStatusEvent(connectorStatus.status),
          ),
      );

      hub.on(ShowConnectorTooltip, () =>
        AppEventTarget.dispatchEvent(new ShowConnectorTooltipEvent()),
      );
    },
    [
      AddNotification,
      MarkedAsRead,
      ReceiveNotifications,
      SetConnectorStatus,
      ShowConnectorTooltip,
      UpdateConnectorStatus,
    ],
  );

  const deregisterListeners = useCallback(
    (hub: HubConnection | undefined) => {
      if (!hub) return;

      hub.off(AddNotification);
      hub.off(ReceiveNotifications);
      hub.off(MarkedAsRead);
      hub.off(SetConnectorStatus);
      hub.off(UpdateConnectorStatus);
      hub.off(ShowConnectorTooltip);
    },
    [
      AddNotification,
      MarkedAsRead,
      ReceiveNotifications,
      SetConnectorStatus,
      ShowConnectorTooltip,
      UpdateConnectorStatus,
    ],
  );

  return { registerListeners, deregisterListeners };
};
